import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, LogIn } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { authApi } from '@/api/auth';
import { Field } from '@/components/ui/field';
import { ApiError } from '@/lib/api-client';
import { loginSchema, type LoginInput } from '@/lib/form-schemas';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
  });

  const login = useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      navigate('/dashboard', { replace: true });
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await login.mutateAsync(values);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Sign in failed');
    }
  });

  useEffect(() => {
    if (submitError) errorRef.current?.focus();
  }, [submitError]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-mb-bg p-6">
      <div className="mb-card p-10 max-w-md w-full">
        <div className="flex flex-col items-center text-center">
          <div className="h-12 w-12 rounded-lg bg-mb-accent-soft text-mb-accent-press flex items-center justify-center mb-4">
            <LogIn className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <h1 className="font-display text-xl font-bold text-mb-text">
            Sign in to MailingBees
          </h1>
          <p className="text-sm text-mb-text-muted mt-1">Welcome back.</p>
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-3" noValidate>
          <Field label="Email" required error={form.formState.errors.email?.message}>
            <input
              type="email"
              autoComplete="email"
              className="mb-input"
              placeholder="you@yourcompany.com"
              {...form.register('email')}
            />
          </Field>
          <Field label="Password" required error={form.formState.errors.password?.message}>
            <input
              type="password"
              autoComplete="current-password"
              className="mb-input"
              {...form.register('password')}
            />
          </Field>

          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-xs text-mb-text-muted hover:text-mb-accent-press"
            >
              Forgot password?
            </Link>
          </div>

          {submitError ? (
            <div
              ref={errorRef}
              tabIndex={-1}
              role="alert"
              className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger focus:outline-none"
            >
              {submitError}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={login.isPending || form.formState.isSubmitting}
            className="mb-btn-primary w-full"
          >
            {login.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Sign in
          </button>
        </form>

        <p className="text-xs text-mb-text-muted text-center mt-6">
          New here?{' '}
          <Link to="/signup" className="text-mb-accent-press hover:underline">
            Create an organization
          </Link>
        </p>
      </div>
    </div>
  );
}
